/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import { useState } from "react";
import type { ComponentProps, ReactNode } from "react";

// @mui material components
import Snackbar from "@mui/material/Snackbar";
import type { SnackbarProps } from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Fade from "@mui/material/Fade";

// Vision UI Dashboard PRO React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiAlert from "components/VuiAlert";

// Vision UI Dashboard PRO React base styles
import typography from "assets/theme/base/typography";

// Custom styles for the VuiSnackbar
import VuiSnackbarIconRoot from "./VuiSnackbarIconRoot";

import {copy} from "utils/utils"

type OwnerState = ComponentProps<typeof VuiSnackbarIconRoot>["ownerState"];

// Everything else (open, onClose, sx...) goes to the MUI snackbar.
type CustomSnackbarProps = Omit<SnackbarProps, "color" | "title" | "content" | "children" | "action"> &
  Partial<OwnerState> & {
    icon: ReactNode;
    title: string;
    dateTime?: ReactNode;
    content: ReactNode;
    close: () => void;
    // Keeps the content's line breaks and spacing.
    pre?: boolean;
    copyOption?: boolean;
    // Shows Confirm / Cancel buttons; Confirm runs it.
    confirmActions?: () => void;
    warning?: ReactNode;
  };

function CustomSnackbar({ color = "info", icon, title, dateTime, content, close, pre, copyOption, confirmActions, warning, ...rest }: CustomSnackbarProps) {
  const [isCopied, setIsCopied] = useState(false);

  const { size } = typography;
  let titleColor = "white" as const;
  let dateTimeColor = "white" as const;
  let dividerColor = true;

  const copyFunction = (text: ReactNode) => {
    let resp = copy(String(text));
    if (resp === true) {
      setIsCopied(true);

      setTimeout(() => setIsCopied(false), 3000);
    }
  }

  return (
    <Snackbar
      TransitionComponent={Fade}
      autoHideDuration={3600000}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      {...rest}
      action={
        <IconButton size="small" aria-label="close" color="inherit" onClick={close}>
          <Icon fontSize="small">close</Icon>
        </IconButton>
      }
    >
      <VuiBox
        bgColor={color}
        minWidth="21.875rem"
        maxWidth="100%"
        shadow="md"
        borderRadius="lg"
        p={1}
      >
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={1.5}>
          <VuiBox display="flex" alignItems="center" lineHeight={0}>
            <VuiSnackbarIconRoot fontSize="small" ownerState={{ color }}>
              {icon}
            </VuiSnackbarIconRoot>
            <VuiTypography variant="button" fontWeight="medium" color={titleColor}>
              {title}
            </VuiTypography>
          </VuiBox>
          <VuiBox display="flex" alignItems="center" lineHeight={0}>
            <VuiTypography variant="caption" color={dateTimeColor}>
              {dateTime}
            </VuiTypography>
            <Icon
              sx={{
                color: ({ palette: { dark, white } }) =>
                  color === "light" ? dark.main : white.main,
                fontWeight: ({ typography: { fontWeightBold } }) => fontWeightBold,
                cursor: "pointer",
                marginLeft: 2,
                transform: "translateY(-1px)",
              }}
              onClick={close}
            >
              close
            </Icon>
          </VuiBox>
        </VuiBox>
        <Divider sx={{ margin: 0 }} light={dividerColor} />
        <VuiBox p={1.5} color={color === "light" ? "text" : "white"} fontSize={size.sm} style={pre ? { whiteSpace: "pre-wrap", overflowWrap: "anywhere" } : undefined}>
          {content}
        </VuiBox>
        <VuiBox p={1.5}>
          {copyOption && (
            <VuiButton
              variant="contained"
              color="white"
              onClick={() => copyFunction(content)}
            >
              {isCopied ? <><Icon sx={{ fontWeight: "bold" }}>check</Icon>COPIED</> : <><Icon sx={{ fontWeight: "bold" }}>copy</Icon>COPY</>}
            </VuiButton>
          )}

          {warning && (
            <VuiAlert color="error" fontSize={size.sm}>{warning}</VuiAlert>
          )}

          {confirmActions && (
            <VuiBox display="flex" justifyContent="start" alignItems="center">
              <VuiButton
                variant="contained"
                color="success"
                onClick={confirmActions}
                style={{marginRight: 10}}
              >
                Confirm
              </VuiButton>
              <VuiButton
                variant="contained"
                color="error"
                onClick={close}
              >
                Cancel
              </VuiButton>
            </VuiBox>
          )}
        </VuiBox>
      </VuiBox>
    </Snackbar>
  );
}

export default CustomSnackbar;
